import { getCurrencySymbol } from './currency';
import type { OpeningHourRow } from './foodMasterImages';
import { hasRestaurantOpeningHourErrors, validateRestaurantOpeningHours } from './restaurantOpeningHours';
import type { RestaurantFormState } from '../components/food/restaurantMasterTypes';
import { RESTAURANT_MASTER_TABS } from '../components/food/restaurantMasterTypes';
import { validatePersonName } from './personFieldValidation';

export type RestaurantFormTabId = (typeof RESTAURANT_MASTER_TABS)[number]['id'];

export type RestaurantFormIssue = {
  tabId: RestaurantFormTabId;
  tabIndex: number;
  field: string;
  message: string;
};

/** Restaurant display names: letters and spaces only (aligned with zone / person name rules). */
export const RESTAURANT_NAME_MIN = 2;
export const RESTAURANT_NAME_MAX = 80;

export function validateRestaurantName(value: string): string | undefined {
  return validatePersonName(value, 'Restaurant name', {
    required: true,
    min: RESTAURANT_NAME_MIN,
    max: RESTAURANT_NAME_MAX,
  });
}

export function validateCommissionValue(
  type: 'percentage' | 'fixed',
  value: number,
): string | undefined {
  if (!Number.isFinite(value)) return 'Commission value is required';
  if (value < 0) return 'Commission value cannot be negative';
  if (type === 'percentage' && value > 100) {
    return 'Percentage commission must be between 0 and 100';
  }
  return undefined;
}

export function commissionValueHelperText(type: 'percentage' | 'fixed'): string {
  if (type === 'percentage') {
    return 'Allowed range: 0–100%. Example: 15 means the platform takes 15% of each order value.';
  }
  return `Fixed amount in ${getCurrencySymbol()} charged per order (0 or greater). Example: 25 means ${getCurrencySymbol()}25 per order.`;
}

export const RESTAURANT_MIN_ORDER_MIN = 1;
export const RESTAURANT_MIN_ORDER_MAX = 99_999;

export function validateRestaurantMinOrderAmount(value: unknown): string | undefined {
  if (value === '' || value == null) return 'Minimum order amount is required';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Enter a valid minimum order amount';
  if (n <= 0) return `Minimum order must be greater than ${getCurrencySymbol()}0`;
  if (n < RESTAURANT_MIN_ORDER_MIN) {
    return `Minimum order must be at least ${getCurrencySymbol()}${RESTAURANT_MIN_ORDER_MIN}`;
  }
  if (n > RESTAURANT_MIN_ORDER_MAX) {
    return `Minimum order cannot exceed ${getCurrencySymbol()}${RESTAURANT_MIN_ORDER_MAX.toLocaleString('en-IN')}`;
  }
  if (!Number.isInteger(n)) return 'Minimum order must be a whole rupee amount';
  return undefined;
}

export function validateRestaurantServiceModes(form: {
  deliveryEnabled?: boolean;
  takeawayEnabled?: boolean;
  dineInEnabled?: boolean;
}): string | undefined {
  if (form.deliveryEnabled || form.takeawayEnabled || form.dineInEnabled) return undefined;
  return 'Enable at least one ordering mode: Delivery, Takeaway, or Dine In';
}

type ValidateRestaurantFormInput = {
  form: RestaurantFormState;
  duplicateErrors: { name?: string; slug?: string };
  serverFieldErrors: Record<string, string>;
  phoneError?: string;
  supportPhoneError?: string;
  emailError?: string;
  editing?: boolean;
  complianceRequired?: {
    gstNumber?: boolean;
    fssaiNumber?: boolean;
    tradeLicenseNumber?: boolean;
  };
  documentAvailability?: {
    gst: string;
    fssai: string;
    tradeLicense: string;
  } | null;
};

export function validateRestaurantForm(input: ValidateRestaurantFormInput): RestaurantFormIssue[] {
  const {
    form,
    duplicateErrors,
    serverFieldErrors,
    phoneError,
    supportPhoneError,
    emailError,
    editing = false,
    complianceRequired = {},
    documentAvailability = null,
  } = input;

  const issues: RestaurantFormIssue[] = [];
  const tabIndex = (id: RestaurantFormTabId) => RESTAURANT_MASTER_TABS.findIndex((t) => t.id === id);

  const push = (tabId: RestaurantFormTabId, field: string, message: string) => {
    issues.push({ tabId, tabIndex: tabIndex(tabId), field, message });
  };

  const nameFormatError = validateRestaurantName(form.name);
  if (nameFormatError) {
    push('basic', 'Restaurant Name', nameFormatError);
  }
  if (duplicateErrors.name || serverFieldErrors.name) {
    push('basic', 'Restaurant Name', duplicateErrors.name || serverFieldErrors.name || 'Duplicate restaurant name');
  }
  if (!editing && (duplicateErrors.slug || serverFieldErrors.slug)) {
    push('basic', 'Slug', duplicateErrors.slug || serverFieldErrors.slug || 'Duplicate slug');
  }
  if (!form.merchantId.trim()) {
    push('basic', 'Merchant Account', 'Select a merchant account');
  }
  if (!form.zoneCode.trim()) {
    push('basic', 'Zone', 'Select an operational zone');
  }
  const lat = Number(form.latitude);
  const lng = Number(form.longitude);
  if (!form.latitude.trim() || !form.longitude.trim() || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    push('basic', 'Location', 'Pin the restaurant location on the map or search an address');
  }
  if (phoneError) {
    push('basic', 'Registered Phone', phoneError);
  }
  if (supportPhoneError) {
    push('basic', 'Support Number', supportPhoneError);
  }
  if (emailError) {
    push('basic', 'Registered Email', emailError);
  }

  if (!form.restaurantTypeCode) {
    push('business', 'Business Type', 'Select a business type');
  }
  if (!form.cuisineTypeCodes.length) {
    push('business', 'Cuisines', 'Select at least one cuisine (first selected is primary)');
  }
  if (complianceRequired.gstNumber && !form.gstNumber.trim()) {
    push('business', 'GST Number', 'GST Number is required by Merchant Document rules');
  }
  if (complianceRequired.fssaiNumber && !form.fssaiNumber.trim()) {
    push('business', 'FSSAI Number', 'FSSAI Number is required by Merchant Document rules');
  }
  if (complianceRequired.tradeLicenseNumber && !form.tradeLicenseNumber.trim()) {
    push('business', 'Trade License', 'Trade License is required by Merchant Document rules');
  }

  const minOrderError = validateRestaurantMinOrderAmount(form.minOrderAmount);
  if (minOrderError) {
    push('settings', 'Min Order', minOrderError);
  }
  const serviceModesError = validateRestaurantServiceModes(form);
  if (serviceModesError) {
    push('settings', 'Ordering Modes', serviceModesError);
  }

  const openingHourErrors = validateRestaurantOpeningHours(form.openingHours);
  if (hasRestaurantOpeningHourErrors(openingHourErrors)) {
    push('timing', 'Opening Hours', 'Fix opening hours — close time must be after open time on each open day');
  }

  if (form.revenueModel === 'subscription' && !form.partnerSubscriptionPlanId) {
    push('approval', 'Merchant Subscription Plan', 'Select a subscription plan for subscription-based revenue');
  }

  if (form.revenueModel === 'commission') {
    const commissionError = validateCommissionValue(form.vendorCommissionType, form.vendorCommissionValue);
    if (commissionError) {
      push('approval', 'Commission Value', commissionError);
    }
  }

  if (form.verificationStatus === 'approved') {
    if (complianceRequired.gstNumber && !form.gstNumber.trim()) {
      push('approval', 'Approval Status', 'Cannot approve — GST Number is required (complete Business tab / documents)');
    }
    if (complianceRequired.fssaiNumber && !form.fssaiNumber.trim()) {
      push('approval', 'Approval Status', 'Cannot approve — FSSAI Number is required (complete Business tab / documents)');
    }
    if (complianceRequired.tradeLicenseNumber && !form.tradeLicenseNumber.trim()) {
      push('approval', 'Approval Status', 'Cannot approve — Trade License is required (complete Business tab / documents)');
    }
    if (form.merchantId && documentAvailability) {
      if (complianceRequired.gstNumber && documentAvailability.gst !== 'approved') {
        push(
          'approval',
          'Approval Status',
          `Cannot approve — GST document is ${documentAvailability.gst} (approve in Documents first)`,
        );
      }
      if (complianceRequired.fssaiNumber && documentAvailability.fssai !== 'approved') {
        push(
          'approval',
          'Approval Status',
          `Cannot approve — FSSAI document is ${documentAvailability.fssai} (approve in Documents first)`,
        );
      }
      if (complianceRequired.tradeLicenseNumber && documentAvailability.tradeLicense !== 'approved') {
        push(
          'approval',
          'Approval Status',
          `Cannot approve — Trade License document is ${documentAvailability.tradeLicense} (approve in Documents first)`,
        );
      }
    } else if (!form.merchantId) {
      push('approval', 'Approval Status', 'Cannot approve — link a Merchant Account and complete required documents first');
    }
  }

  return issues;
}

export function issuesByTab(issues: RestaurantFormIssue[]): Record<RestaurantFormTabId, RestaurantFormIssue[]> {
  const grouped = {} as Record<RestaurantFormTabId, RestaurantFormIssue[]>;
  for (const tab of RESTAURANT_MASTER_TABS) {
    grouped[tab.id] = [];
  }
  for (const issue of issues) {
    grouped[issue.tabId].push(issue);
  }
  return grouped;
}

export function tabErrorCounts(issues: RestaurantFormIssue[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const issue of issues) {
    counts[issue.tabIndex] = (counts[issue.tabIndex] || 0) + 1;
  }
  return counts;
}
